/**
 * Reiter *Dokumente* eines Objekts **und** Bereich *Dokumente* (Spec 09,
 * „Was neu entsteht": „Bereich und Reiter teilen sich eine
 * Listenkomponente"). Welche Abfrage läuft (`?scope=` oder `?unfiled=1`/
 * `?category=`/`?q=`), entscheidet der Aufrufer (Container pro Bildschirm
 * besitzt die Query) — diese Komponente bekommt nur das Ergebnis.
 *
 * `document-add` ist an **beiden** Stellen die primäre Aktion (Spec 09,
 * „Aktionen": „ja" in beiden Zeilen) — anders als `task-add`, das im
 * Bereich *Aufgaben* fehlt: Ein Dokument lässt sich immer aufnehmen, auch
 * ohne festes Objekt (der Eingang).
 *
 * Anlegen und Ändern teilen sich `document-sheet` in **einem** Mount
 * (`sheetState`), dieselbe Bauform wie `TaskList`.
 */

import { useState } from 'react'
import type { InfiniteData, UseInfiniteQueryResult, UseQueryResult } from '@tanstack/react-query'

import { EmptyState } from '@/components/states/EmptyState'
import { ErrorState } from '@/components/states/ErrorState'
import { ApiError } from '@/lib/api'
import { DocumentListSkeleton } from '@/features/documents/DocumentListSkeleton'
import { DocumentSheet } from '@/features/documents/DocumentSheet'
import { DocumentListBody } from '@/features/documents/DocumentListBody'
import type { DocumentRow as DocumentRowData, DocumentsPage } from '@/features/documents/api'

type SheetState = { readonly mode: 'create' } | { readonly mode: 'edit'; readonly document: DocumentRowData }

interface DocumentListProps {
  readonly query: UseQueryResult<DocumentsPage> | UseInfiniteQueryResult<InfiniteData<DocumentsPage>>
  /** Nur am Objekt gesetzt — fixiert „Hängt an" im Sheet als Text
   * (`ARCHITECTURE.md` 6.4, „Aus dem Kontext anlegen"). `undefined` im
   * Bereich, dort ist „Hängt an" `document-subject-picker`. */
  readonly fixedSubjectId?: string
  readonly fixedSubjectLabel?: string
  readonly emptyTitle: string
  readonly emptyHint: string
  /** Text des Knopfs `document-add`; Vorgabe „+ Dokument". Am Zähler heißt er
   * „Dokument verknüpfen" (Spec 46c). */
  readonly addLabel?: string
  /** `document-add` als umrandete Taste statt Akzentfläche — wo schon eine
   * andere primäre Aktion auf dem Bildschirm steht (Zählerdetail, Spec 46c). */
  readonly isAddSecondary?: boolean
  /** Ohne Recht zum Verknüpfen entfällt `document-add` (Zählerdetail:
   * `can_edit` des Servers). Vorgabe `true` — Bestandsaufrufer unverändert. */
  readonly canAdd?: boolean
  /** Karten statt Tabelle, auch am Schreibtisch — in schmaler Spalte (Zähler). */
  readonly compact?: boolean
  /** Mit Suchbegriff sortiert der Server nach Relevanz — dann keine
   * Gruppenüberschriften (Spec 35, Fachregel 2). */
  readonly isSearching?: boolean
}

export function DocumentList({
  query,
  fixedSubjectId,
  fixedSubjectLabel,
  emptyTitle,
  emptyHint,
  addLabel = '+ Dokument',
  isAddSecondary = false,
  canAdd = true,
  compact = false,
  isSearching = false,
}: DocumentListProps) {
  const [sheetState, setSheetState] = useState<SheetState | null>(null)
  // Die optimistische Zeile bleibt stehen, bis `POST /documents` durch ist —
  // nicht nur bis der `PUT` fertig ist (Spec 09, „Während der Text gelesen
  // wird"). `DocumentSheet` meldet Beginn und Ende über diese beiden
  // Rückrufe, statt selbst in den Query-Cache zu schreiben: Anders als beim
  // Objektbaum (`snapshotAndInsertPending`) ist die Zielliste hier
  // cursorpaginiert und ihr aktiver Schlüssel hängt am Filter des
  // Aufrufers — dieser Container kennt ihn bereits.
  const [pendingFileName, setPendingFileName] = useState<string | null>(null)

  const editingDocument = sheetState?.mode === 'edit' ? sheetState.document : null
  const accessError = query.error instanceof ApiError && (query.error.isForbidden || query.error.isUnauthenticated)
  const hasReadableData = query.data !== undefined && !accessError
  const pageItems = !hasReadableData ? [] : 'pages' in query.data ? query.data.pages.flatMap((page) => page.items) : query.data.items
  const documents = [...new Map(pageItems.map((item) => [item.id, item])).values()]
  const pagedQuery = 'fetchNextPage' in query ? query : undefined

  const addButton = canAdd ? (
    <button
      type="button"
      data-testid="document-add"
      onClick={() => {
        setSheetState({ mode: 'create' })
      }}
      className={
        isAddSecondary
          ? 'border-border text-text hover:bg-bg focus-visible:border-accent rounded-card text-body min-h-(--tap-min) border px-3 transition-colors duration-(--duration-fast) ease-(--ease-out)'
          : 'bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out)'
      }
    >
      {addLabel}
    </button>
  ) : undefined

  return (
    <div data-testid="document-list" className="flex flex-col">
      {query.isPending && <DocumentListSkeleton />}

      {(accessError || (query.isError && !hasReadableData)) && (
        <ErrorState
          testId="document-list-error"
          retryTestId="document-list-retry"
          error={query.error}
          onRetry={() => {
            void query.refetch()
          }}
        />
      )}

      {hasReadableData && documents.length === 0 && pendingFileName === null && (
        <EmptyState testId="document-list-empty" title={emptyTitle} hint={emptyHint} action={addButton} />
      )}

      {hasReadableData && (documents.length > 0 || pendingFileName !== null) && (
        <>
          {addButton && <div className="flex justify-end px-(--page-x) py-2">{addButton}</div>}
          <DocumentListBody
            documents={documents}
            isSearching={isSearching}
            compact={compact}
            pendingFileName={pendingFileName}
            onSelect={(document) => {
              setSheetState({ mode: 'edit', document })
            }}
          />
          {pagedQuery?.hasNextPage && (
            <button
              type="button"
              data-testid="object-facet-dokumente-more"
              disabled={pagedQuery.isFetchingNextPage}
              onClick={() => {
                void pagedQuery.fetchNextPage()
              }}
              className="border-border text-text m-2 self-start border px-3"
            >
              Mehr laden
            </button>
          )}
        </>
      )}

      <DocumentSheet
        open={sheetState !== null}
        onOpenChange={(open) => {
          if (!open) setSheetState(null)
        }}
        {...(fixedSubjectId !== undefined ? { fixedSubjectId } : {})}
        {...(fixedSubjectLabel !== undefined ? { fixedSubjectLabel } : {})}
        document={editingDocument}
        onUploadStart={(fileName) => {
          setPendingFileName(fileName)
        }}
        onUploadSettled={() => {
          setPendingFileName(null)
        }}
      />
    </div>
  )
}
